import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Server, 
  ClipboardList, 
  CheckSquare, 
  AlertTriangle, 
  AlertCircle, 
  Users, 
  Folder,
  UserCheck,
  ChevronRight
} from 'lucide-react';
import { useMyResponsibilities, ResponsibilityCategory } from '@/hooks/queries/useMyResponsibilities';
import { Skeleton } from '@/components/ui/skeleton';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface ResponsibilityRow {
  key: string;
  icon: React.ElementType;
  label: string;
  description: string;
  path: string;
  data: ResponsibilityCategory;
}

function UrgencyIndicator({ hasOverdue, hasDueSoon }: { hasOverdue?: boolean; hasDueSoon?: boolean }) {
  if (hasOverdue) {
    return <span className="h-2 w-2 rounded-full bg-red-500" title="Needs attention" />;
  }
  if (hasDueSoon) {
    return <span className="h-2 w-2 rounded-full bg-yellow-500" title="Due soon" />;
  }
  return null;
}

export function MyResponsibilitiesCard() {
  const { data: responsibilities, isLoading } = useMyResponsibilities();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCheck className="h-5 w-5" />
            My Responsibilities
          </CardTitle>
          <CardDescription>Items assigned to you</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {[1, 2, 3, 4, 5, 6, 7].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  const rows: ResponsibilityRow[] = [
    {
      key: 'assets',
      icon: Server,
      label: 'Assets',
      description: 'Sites assigned to you',
      path: '/assets?filter=assigned',
      data: responsibilities?.assets || { total: 0 },
    },
    {
      key: 'tasks',
      icon: ClipboardList,
      label: 'Tasks',
      description: 'Active tasks assigned',
      path: '/tasks?filter=assigned',
      data: responsibilities?.tasks || { total: 0 },
    },
    {
      key: 'controls',
      icon: CheckSquare,
      label: 'Controls',
      description: 'Controls to implement',
      path: '/controls?filter=assigned',
      data: responsibilities?.controls || { total: 0 },
    },
    {
      key: 'risks',
      icon: AlertTriangle,
      label: 'Risks',
      description: 'Open risks assigned',
      path: '/risks?filter=assigned',
      data: responsibilities?.risks || { total: 0 },
    },
    {
      key: 'incidents',
      icon: AlertCircle,
      label: 'Incidents',
      description: 'Open incidents',
      path: '/incidents?filter=assigned',
      data: responsibilities?.incidents || { total: 0 },
    },
    {
      key: 'suppliers',
      icon: Users,
      label: 'Suppliers',
      description: 'Suppliers to review',
      path: '/suppliers?filter=assigned',
      data: responsibilities?.suppliers || { total: 0 },
    },
    {
      key: 'evidence',
      icon: Folder,
      label: 'Evidence',
      description: 'Evidence items assigned',
      path: '/evidence?filter=assigned',
      data: responsibilities?.evidence || { total: 0 },
    },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5" />
              My Responsibilities
            </CardTitle>
            <CardDescription>Items assigned to you</CardDescription>
          </div>
          <Badge variant="secondary">{responsibilities?.totalCount || 0} total</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-1">
        {rows.map((row) => {
          const Icon = row.icon;
          const isEmpty = row.data.total === 0;
          
          return (
            <button
              key={row.key}
              onClick={() => navigate(row.path)}
              className={cn(
                "w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors text-left group",
                isEmpty 
                  ? "opacity-50 hover:opacity-70 hover:bg-accent/50" 
                  : "hover:bg-accent"
              )}
            >
              <div className="flex items-center gap-3">
                <Icon className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">{row.label}</p>
                  <p className="text-xs text-muted-foreground">{row.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <UrgencyIndicator 
                  hasOverdue={row.data.hasOverdue} 
                  hasDueSoon={row.data.hasDueSoon} 
                />
                <Badge variant="secondary" className="min-w-[2rem] justify-center">
                  {row.data.total}
                </Badge>
                <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </button>
          );
        })}
      </CardContent>
    </Card>
  );
}
